import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Inventory,
  SmartToy,
  Assessment,
  Notifications,
  Settings,
  Logout,
  ChevronLeft,
  AccountCircle,
  Task,
  Science,
  Chat,
  People,
  Handshake,
  Help,
  CloudUpload,
  Extension,
  SwapHoriz,
  Business,
  AccountTree,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ascinAuth } from '../../services/ascinAuthApi';
import GlobalSearch from '../GlobalSearch';
import NotificationsPanel from '../NotificationsPanel';

const drawerWidth = 260;

interface AppLayoutProps {
  children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const menuItems = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
    { text: 'AI Chat', icon: <Chat />, path: '/chat' },
    { text: 'HITL Tasks', icon: <Task />, path: '/tasks', badge: 3 },
    { text: 'AI Agents', icon: <SmartToy />, path: '/agents' },
    { text: 'Simulation', icon: <Science />, path: '/simulation' },
    { text: 'Supply Chain', icon: <Inventory />, path: '/supply-chain' },
    { text: 'CRM', icon: <People />, path: '/crm/contacts' },
    { text: 'Negotiations', icon: <Handshake />, path: '/negotiations' },
    { text: 'Analytics', icon: <Assessment />, path: '/analytics' },
    { text: 'MESH Resilience', icon: <AccountTree />, path: '/mesh' },
    { text: 'Data Import', icon: <CloudUpload />, path: '/data-import' },
    { text: 'Integrations', icon: <Extension />, path: '/integrations' },
    { text: 'Migration', icon: <SwapHoriz />, path: '/migration' },
    { text: 'Company Profile', icon: <Business />, path: '/company-profile' },
    { text: 'Settings', icon: <Settings />, path: '/settings' },
    { text: 'Help & FAQ', icon: <Help />, path: '/help' },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    const wasAscin = ascinAuth.isAuthenticated();
    // Clear ASCIN multi-tenant session
    ascinAuth.logout().catch(() => {});
    // Clear Firebase session (if any)
    try { logout(); } catch { /* noop */ }
    navigate(wasAscin ? '/ascin-login' : '/login');
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          color: 'white',
        }}
      >
        <SmartToy sx={{ fontSize: 32 }} />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          EF-Link
        </Typography>
      </Box>

      <Divider />

      {/* Navigation */}
      <List sx={{ flexGrow: 1, px: 1, py: 2 }}>
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => handleMenuClick(item.path)}
                sx={{
                  borderRadius: 2,
                  bgcolor: isActive ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                  color: isActive ? theme.palette.primary.main : 'text.primary',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isActive ? theme.palette.primary.main : 'inherit',
                    minWidth: 40,
                  }}
                >
                  {item.badge ? (
                    <Badge badgeContent={item.badge} color="error">
                      {item.icon}
                    </Badge>
                  ) : (
                    item.icon
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: isActive ? 600 : 400,
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Divider />

      {/* User Profile */}
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 1.5,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            cursor: 'pointer',
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.1),
            },
          }}
          onClick={handleProfileMenuOpen}
        >
          <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
            {(ascinAuth.getUser()?.full_name || ascinAuth.getUser()?.email || user?.name || 'U').charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
              {ascinAuth.getUser()?.full_name || ascinAuth.getUser()?.email || user?.name || 'User'}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {ascinAuth.getUser()?.email || user?.email || 'user@example.com'}
            </Typography>
            {ascinAuth.getTenant() && (
              <Typography variant="caption" color="primary" noWrap display="block" sx={{ fontWeight: 600 }}>
                {ascinAuth.getTenant()!.name} · {ascinAuth.getTenant()!.plan}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          bgcolor: 'white',
          color: 'text.primary',
          boxShadow: theme.shadows[1],
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" noWrap component="div" sx={{ mr: 3 }}>
            {menuItems.find((item) => item.path === location.pathname)?.text || 'EF-Link'}
          </Typography>

          {/* Global Search */}
          <Box sx={{ flexGrow: 1, maxWidth: 500, mr: 2 }}>
            <GlobalSearch />
          </Box>

          {/* Notifications */}
          <NotificationsPanel />

          {/* Settings */}
          <IconButton color="inherit" onClick={() => navigate('/settings')}>
            <Settings />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
          bgcolor: alpha(theme.palette.grey[100], 0.5),
          minHeight: '100vh',
        }}
      >
        {children}
      </Box>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/settings'); }}>
          <ListItemIcon>
            <AccountCircle fontSize="small" />
          </ListItemIcon>
          Profile
        </MenuItem>
        <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/settings'); }}>
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>
          Settings
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default AppLayout;
